import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { createExperienceWithMetaType } from '@jira/polaris__lib-analytics/src/createJpdExperience.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
import { EntityLimitError } from '@jira/polaris__lib-errors/src/EntityLimitError.tsx';
import { ENTITY_LIMIT_ERROR_TYPE } from '@jira/polaris__lib-errors/src/LimitType.types.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { type Props, type State, DEFAULT_OPTION_WEIGHT } from './FieldsUpdate.types.tsx';
import { loadFields } from './loadFields.tsx';

const fieldOptionUpdateExperience = createExperienceWithMetaType(
	experience.fieldsSidebar.fieldOptionUpdate,
	'addOption',
	(err) => `FieldOptionUpdate: ${err?.message ?? 'generic error'}`,
);

export const addOption =
	(fieldKey: FieldKey, newOption: string) =>
	(
		{ getState, setState, dispatch }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	): Promise<number | undefined> => {
		if (projectId === undefined) {
			return Promise.resolve(undefined);
		}

		fieldOptionUpdateExperience.start();

		return fieldRemote
			.addFieldOption({
				fieldKey,
				newOption,
			})
			.then((jiraOptionId: number) => {
				dispatch(loadFields());

				const state = getState();

				const newOptions = [
					...(state?.fields?.[fieldKey]?.options || []),
					{
						id: String(jiraOptionId),
						jiraOptionId: String(jiraOptionId),
						value: newOption,
						weight: DEFAULT_OPTION_WEIGHT,
					},
				];

				const fields = {
					...(state?.fields ?? {}),
					[fieldKey]: {
						...state?.fields?.[fieldKey],
						options: newOptions,
					},
				};

				setState({ fields });

				fieldOptionUpdateExperience.success();

				fireCompoundAnalyticsEvent.FieldConfig.optionCreatedSuccess(createAnalyticsEvent({}), {
					issueFieldKey: fieldKey,
					issueFieldType: fields[fieldKey].type,
					fieldValueId: jiraOptionId,
				});

				return jiraOptionId;
			})
			.catch((err: Error) => {
				let ufoExperienceResolved = false;
				fireCompoundAnalyticsEvent.FieldConfig.optionCreatedFailure(createAnalyticsEvent({}));

				let error = err;
				if (err instanceof FetchError) {
					const { message, statusCode } = err;
					if (statusCode === 409) {
						error = new EntityLimitError(
							message,
							[{ error: message }],
							statusCode,
							ENTITY_LIMIT_ERROR_TYPE.OPTIONS_PER_FIELD,
						);
						fieldOptionUpdateExperience.abort(err);
						ufoExperienceResolved = true;
					}
				}

				if (!ufoExperienceResolved) {
					fieldOptionUpdateExperience.failure(err);
					ufoExperienceResolved = true;
				}

				onFieldUpdateFailed(error);
				return undefined;
			});
	};
